// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo } from 'react';

import { type EventsWeeklySummaryResp, formatDate } from '@evolvemep/api-ts';
import type { AgChartOptions } from 'ag-charts-enterprise';
import { parseISO } from 'date-fns';

import { ChartRenderer } from 'modules/Shop/Insights/RevitUsage/components/ChartRenderer';
import type { AnalyticsChartProps } from 'modules/Shop/Insights/RevitUsage/types';

import { useAnalyticsChart } from './useAnalyticsChart';

export const EventsByDateChart = ({ showDemo, ...props }: AnalyticsChartProps<EventsWeeklySummaryResp>) => {
  const [data, loading] = useAnalyticsChart('usage-stats/v1/reporting/events/weekly', props);

  const formattedData = useMemo(
    () =>
      data?.weeklyEvents.map((e) => ({
        ...e,
        startOfWeek: parseISO(e.startOfWeek),
      })),
    [data?.weeklyEvents],
  );

  const options = useMemo<AgChartOptions>(
    () => ({
      data: formattedData,
      title: {
        enabled: !showDemo,
        text: 'Events by Date',
        textAlign: 'left',
      },
      series: [
        {
          type: 'line',
          xKey: 'startOfWeek',
          yKey: 'totalEvents',
          stroke: '#32db95',
          strokeWidth: 3,
          marker: {
            enabled: false,
          },
          tooltip: {
            renderer: ({ datum }: { datum: NonNullable<typeof formattedData>[number] }) => ({
              heading: `Week of ${formatDate(datum.startOfWeek, 'MM/dd/yyyy')}`,
              data: [
                {
                  label: 'Total events',
                  value: String(datum.totalEvents),
                },
              ],
            }),
          },
        },
      ],
      axes: {
        x: {
          position: 'bottom',
          type: 'time',
          interval: {
            step: 'month',
          },
          label: {
            rotation: -35,
            format: '%b %Y',
          },
        },
        y: {
          position: 'left',
          type: 'number',
        },
      },
    }),
    [formattedData, showDemo],
  );

  return <ChartRenderer options={options} loading={loading} minHeight={showDemo ? undefined : 500} />;
};
